import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
} from '@angular/core';

import { IonIcon } from '@ionic/angular';
import { ButtonGetmFill, ButtonGetmSize, ButtonGetmStatus, ButtonGetmType } from '../../enums/button-getm.types';

@Component({
  selector: 'app-button-getm',
  templateUrl: './button-getm.component.html',
  styleUrls: ['./button-getm.component.scss']
})
export class ButtonGetmComponent implements AfterContentInit {

  @Input() type: ButtonGetmType = 'primary';
  @Input() size: ButtonGetmSize = 'medium';
  @Input() status: ButtonGetmStatus = 'default';
  @Input() fill: ButtonGetmFill = 'default';
  @Input() disabled = false;
  @Input() expand = false;
  @Output() buttonClick = new EventEmitter<MouseEvent>();

  /**
   * Detecta todos los ion-icon proyectados en el contenido
   */
  @ContentChildren(IonIcon, { read: ElementRef, descendants: true }) icons!: QueryList<ElementRef>;

  /**
   * Estado interno para tracking de iconos
   */
  private hasStartIcon = false;
  private hasEndIcon = false;

  ngAfterContentInit(): void {
    this.updateIconState();
    // Escuchar cambios dinámicos en los iconos proyectados
    this.icons.changes.subscribe(() => this.updateIconState());
  }

  /**
   * Actualiza el estado de los iconos según el contenido proyectado
   */
  private updateIconState(): void {
    this.hasStartIcon = false;
    this.hasEndIcon = false;

    this.icons.forEach((iconRef: ElementRef) => {
      const slot = iconRef.nativeElement.getAttribute('slot');
      if (slot === 'start') {
        this.hasStartIcon = true;
      } else if (slot === 'end') {
        this.hasEndIcon = true;
      }
    });
  }

  /**
   * Genera las clases CSS para el botón basándose en type, size, status
   */
  public get buttonClass(): string {
    const classes = [
      'btn-getm',
      `btn-getm--${this.type}`,
      `btn-getm--${this.size}`
    ];

    // Agregar clase de estado si no es default
    if (this.status !== 'default') {
      classes.push(`btn-getm--${this.status}`);
    }

    // Si disabled está activo pero status no es disabled, agregar clase
    if (this.disabled && this.status !== 'disabled') {
      classes.push('btn-getm--disabled');
    }

    // Aplicar espaciado automático cuando NO hay iconos
    if (!this.hasStartIcon && !this.hasEndIcon) {
      classes.push('btn-getm--icon-hidden-both');
    } else if (!this.hasStartIcon) {
      classes.push('btn-getm--icon-hidden-start');
    } else if (!this.hasEndIcon) {
      classes.push('btn-getm--icon-hidden-end');
    }

    return classes.join(' ');
  }

  /**
   * Genera las clases del host para el expand
   */
  public get hostClass(): string {
    return this.expand ? 'btn-getm--expand' : '';
  }

  /**
   * Determina si el botón debe estar deshabilitado
   */
  public get isDisabled(): boolean {
    return this.disabled || this.status === 'disabled';
  }

  /**
   * Maneja el evento click del botón
   */
  public onClick(event: MouseEvent): void {
    if (!this.isDisabled) {
      this.buttonClick.emit(event);
    }
  }
}
